import React from "react";
import PropTypes from "prop-types";

import { Field, Control, Label, Input, Title, Button, Box, Column } from "rbx";
import { useSystemCodes } from "../../../../utils/hooks";

import {
  BooleanInput,
  SystemCodeSelect,
  StateSelect,
  DateInput
} from "../../../../components/inputs";

import { VehicleOutOfServiceModal } from "../../../../components/VehicleOutOfService";

const Weights = ({ inputs, onChange, editing }) => (
  <div className="profile-item">
    <Title>Weights</Title>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Overweight Permit</Label>
      </Field.Label>
      <Field.Body>
        <Field className="grid-override" kind="group">
          <Control>
            <Input
              autocomplete="new"
              disabled={!editing}
              name="OverWeightPermit"
              type="text"
              value={inputs.OverWeightPermit}
              onChange={onChange}
            />
          </Control>
          <Control>
            <DateInput
              autocomplete="new"
              disabled={!editing}
              name="OverWeightPermitExpirationDate"
              type="date"
              value={inputs.OverWeightPermitExpirationDate}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Unladen</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <Input
              autocomplete="new"
              disabled={!editing}
              name="Unladen"
              type="number"
              value={inputs.Unladen}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Payload</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <Input
              autocomplete="new"
              disabled={!editing}
              name="Payload"
              type="number"
              value={inputs.Payload}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Trycock Net</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <Input
              autocomplete="new"
              disabled={!editing}
              name="TrycockNet"
              type="number"
              value={inputs.TrycockNet}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
  </div>
);
Weights.propTypes = {
  inputs: PropTypes.object,
  editing: PropTypes.bool,
  onChange: PropTypes.func
};

const Registration = ({ inputs, onChange, editing }) => (
  <div className="profile-item">
    <Title>Registration</Title>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Plate Number</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <Input
              autocomplete="new"
              disabled={!editing}
              name="PlateNumber"
              placeholder="Plate Number"
              type="text"
              value={inputs.PlateNumber}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Registration Number</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <Input
              autocomplete="new"
              disabled={!editing}
              name="RegistrationNumber"
              placeholder="Registration Number"
              type="text"
              value={inputs.RegistrationNumber}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>

    <Field horizontal>
      <Field.Label size="small">
        <Label>Registration State</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <StateSelect
              disabled={!editing}
              label="Registration State"
              name="RegistrationState"
              value={inputs.RegistrationState}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
    <Field horizontal>
      <Field.Label size="small">
        <Label>Registration Exp. Date</Label>
      </Field.Label>
      <Field.Body>
        <Field>
          <Control>
            <DateInput
              disabled={!editing}
              label="Registration Exp. Date"
              name="RegistrationExpirationDate"
              type="date"
              value={inputs.RegistrationExpirationDate}
              onChange={onChange}
            />
          </Control>
        </Field>
      </Field.Body>
    </Field>
  </div>
);
Registration.propTypes = {
  inputs: PropTypes.object,
  onChange: PropTypes.func,
  editing: PropTypes.bool
};
function Profile({ inputs, onChange, editing, setModalOpen }) {
  const TractorOrTrailerSystemCodes =
    useSystemCodes({
      Category: "Fleet",
      CodeName: "VehicleType",
      CodeDescription_in: ["Tractor", "Trailer"]
    })?.data?.map(x => x.RecId) || [];

  return (
    <React.Fragment>
      <Box>
        <Title>Profile</Title>
        <Column.Group>
          <Column size={1}>
            <Control>
              <Label>Vehicle #</Label>
              <Input
                required
                autocomplete="new"
                disabled={!editing}
                name="VehicleNumber"
                type="text"
                value={inputs?.VehicleNumber}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={2}>
            <Control expanded>
              <Label>Description</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="Description"
                type="text"
                value={inputs?.Description}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                required
                icon="truck"
                label="Vehicle Type"
                lookup={{ Category: "Fleet", Code: "VehicleType" }}
                name="VehicleTypeRecIdRef"
                readOnly={!editing}
                value={inputs?.VehicleTypeRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Field kind="group">
              <Control expanded>
                <SystemCodeSelect
                  readOnly
                  icon="truck"
                  label="Status"
                  lookup={{ Category: "Fleet", Code: "VehicleStatus" }}
                  name="StatusRecIdRef"
                  value={inputs?.StatusRecIdRef}
                  onChange={onChange}
                />
              </Control>
              {inputs.RecId && (
                <Button
                  color="primary"
                  disabled={!editing}
                  size="small"
                  style={{ marginTop: "25px" }}
                  onClick={() =>
                    setModalOpen(
                      true,
                      <VehicleOutOfServiceModal
                        {...{
                          id: null,
                          vehicleId: inputs.RecId,
                          onComplete: value => {
                            onChange({
                              target: { name: "StatusRecIdRef", value }
                            });
                            setModalOpen(false);
                          }
                        }}
                      />
                    )
                  }
                >
                  {" "}
                  Update
                </Button>
              )}
            </Field>
          </Column>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Category"
                lookup={{ Category: "Fleet", Code: "VehicleCategory" }}
                name="CategoryRecIdRef"
                readOnly={!editing}
                value={inputs?.CategoryRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
        </Column.Group>
        <Column.Group>
          <Column size={3}>
            <Control expanded>
              <Label>Year</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                maxLength="4"
                name="Year"
                type="text"
                value={inputs?.Year}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Make</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="Make"
                type="text"
                value={inputs?.Make}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Model</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="Model"
                type="text"
                value={inputs?.Model}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Serial #</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="SerialNumber"
                type="text"
                value={inputs?.SerialNumber}
                onChange={onChange}
              />
            </Control>
          </Column>
        </Column.Group>
        <Column.Group>
          <Column size={1}>
            <Control>
              <Label>ELD ID</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="ELDVehicleId"
                required={TractorOrTrailerSystemCodes.includes(
                  parseInt(inputs?.VehicleTypeRecIdRef, 10)
                )}
                type="text"
                value={inputs?.ELDVehicleId}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={2}>
            <Field kind="group">
              <Control>
                <Label>Company Owned</Label>
                <BooleanInput
                  disabled={!editing}
                  name="CompanyOwned"
                  value={inputs?.CompanyOwned}
                  onChange={onChange}
                />
              </Control>
            </Field>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Engine</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="Engine"
                type="text"
                value={inputs?.Engine}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Axles</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="Axles"
                type="number"
                value={inputs?.Axles}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Transmission"
                lookup={{ Category: "Fleet", Code: "VehicleTransmission" }}
                name="TransmissionRecIdRef"
                readOnly={!editing}
                value={inputs?.TransmissionRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
        </Column.Group>
        <Column.Group>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Rear"
                lookup={{ Category: "Fleet", Code: "VehicleRear" }}
                name="RearRecIdRef"
                readOnly={!editing}
                value={inputs?.RearRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Body"
                lookup={{ Category: "Fleet", Code: "VehicleBody" }}
                name="BodyRecIdRef"
                readOnly={!editing}
                value={inputs?.BodyRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label># of Diesel Tanks</Label>
              <Input
                autocomplete="new"
                disabled={!editing}
                name="NumDieselTanks"
                type="number"
                value={inputs.NumDieselTanks}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Tire Size"
                lookup={{ Category: "Fleet", Code: "VehicleTireSize" }}
                name="TireSizeRecIdRef"
                readOnly={!editing}
                value={inputs?.TireSizeRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
        </Column.Group>
        <Column.Group>
          <Column size={3}>
            <Control expanded>
              <SystemCodeSelect
                icon="truck"
                label="Wheel Size"
                lookup={{ Category: "Fleet", Code: "VehicleWheelSize" }}
                name="WheelSizeRecIdRef"
                readOnly={!editing}
                value={inputs?.WheelSizeRecIdRef}
                onChange={onChange}
              />
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Max. Cold</Label>
              <Field kind="addons">
                <Control expanded>
                  <Input
                    autocomplete="new"
                    disabled={!editing}
                    name="MaxCold"
                    type="number"
                    value={inputs.MaxCold}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Max. Warm</Label>
              <Field kind="addons">
                <Control expanded>
                  <Input
                    autocomplete="new"
                    disabled={!editing}
                    name="MaxWarm"
                    type="number"
                    value={inputs.MaxWarm}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Control>
          </Column>
          <Column size={3}>
            <Control expanded>
              <Label>Odometer</Label>
              <Field kind="addons">
                <Control expanded>
                  <Input
                    autoComplete="new"
                    disabled={!editing}
                    min={0}
                    name="Odometer"
                    type="number"
                    value={inputs.Odometer}
                    onChange={onChange}
                  />
                </Control>
              </Field>
            </Control>
          </Column>
        </Column.Group>
      </Box>
      <div className="vehicle-profile-items">
        <Box style={{ height: "95%" }}>
          <Registration editing={editing} inputs={inputs} onChange={onChange} />
        </Box>
        <Box style={{ height: "95%" }}>
          <Weights editing={editing} inputs={inputs} onChange={onChange} />
        </Box>
      </div>
    </React.Fragment>
  );
}

Profile.propTypes = {
  editing: PropTypes.bool,
  inputs: PropTypes.object,
  onChange: PropTypes.func,
  setModalOpen: PropTypes.func
};
Profile.defaultProps = {
  inputs: {}
};
export default Profile;
